import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { register } from '../api/auth';
import { toFormErrors, type FormErrors } from '../auth/errors';
import { Alert, Button, Input } from '../components/ui';
import { AuthLayout } from './AuthLayout';

const NO_ERRORS: FormErrors = { fields: {}, message: null };

export function RegisterPage() {
  const [form, setForm] = useState({
    display_name: '',
    handle: '',
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  function update(field: keyof typeof form) {
    return (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [field]: event.target.value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors(NO_ERRORS);
    setIsSubmitting(true);

    try {
      await register(form);
      setSent(true);
    } catch (error) {
      setErrors(toFormErrors(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your inbox"
        subtitle="We sent a confirmation link to the address you gave us. Open it to finish setting up your account."
        footer={
          <>
            Already confirmed? <Link to="/login">Sign in</Link>
          </>
        }
      >
        <Alert tone="success" title="Almost there">
          The link expires after a while — if it does, you can ask for a new one from the sign-in
          page.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="A simple coffee. A clear mind. A great analysis."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form className="sc-form" onSubmit={handleSubmit} noValidate>
        {errors.message && <Alert tone="danger">{errors.message}</Alert>}

        <Input
          label="Display name"
          name="display_name"
          autoComplete="name"
          required
          value={form.display_name}
          onChange={update('display_name')}
          error={errors.fields['display_name']}
        />

        <Input
          label="Handle"
          name="handle"
          prefix="@"
          autoComplete="username"
          required
          hint="Lowercase letters, numbers and underscores. This is how people will find you."
          value={form.handle}
          onChange={update('handle')}
          error={errors.fields['handle']}
        />

        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={update('email')}
          error={errors.fields['email']}
        />

        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          required
          hint="At least 10 characters, and not too close to your name or email."
          value={form.password}
          onChange={update('password')}
          error={errors.fields['password']}
        />

        <div className="sc-form__actions">
          <Button type="submit" isLoading={isSubmitting} loadingLabel="Creating account" fullWidth>
            Create account
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
